// src/hooks/useBattleList.js
import { useEffect, useRef, useState } from "react";
import { listBattle } from "../api/battleApi";

import { listCoursePathPoints } from "../api/courseApi";
import { buildGoogleStaticMapUrl } from "../utils/googleStaticMap";

export function useBattleList(initialFilters = {}) {
  const baseFilters = {
    searchKeyword: "",
    searchStartDate: "",
    modeFilter: "",
    statusFilter: "",
    genderType: "",
    userId: null,
    userLat: null,
    userLng: null,

    // ✅ 페이징 기본값
    offset: 0,
    limit: 5,

    ...initialFilters,
  };

  const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_STATIC_MAP_KEY;

  const [filters, setFilters] = useState(baseFilters);
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ 무한스크롤용
  const [hasMore, setHasMore] = useState(true);

  const thumbCacheRef = useRef(new Map());

  const formatStartDatetime = (value) => {
    if (!value) return "";
    const replaced = value.replace("T", " ");
    const ymdhm = replaced.slice(0, 16);
    return ymdhm.replace(/-/g, ".");
  };

  const getCourseThumb = async (courseId) => {
    if (!GOOGLE_KEY || !courseId) return null;

    if (thumbCacheRef.current.has(courseId)) {
      return thumbCacheRef.current.get(courseId);
    }

    try {
      const points = await listCoursePathPoints(courseId);
      if (!points || points.length === 0) {
        thumbCacheRef.current.set(courseId, null);
        return null;
      }

      const url = buildGoogleStaticMapUrl(points, {
        apiKey: GOOGLE_KEY,
        size: "200x200",
        zoom: 15,
        maxPoints: 120,
      });

      const finalUrl = url || null;
      thumbCacheRef.current.set(courseId, finalUrl);
      return finalUrl;
    } catch (e) {
      thumbCacheRef.current.set(courseId, null);
      return null;
    }
  };

  const fetchListBattle = async () => {
    // ✅ 더 가져올 게 없으면 호출 막기
    if (!hasMore && (filters.offset ?? 0) > 0) return;

    setLoading(true);
    try {
      const res = await listBattle(filters);
      const rawList = res.data || [];

      // ✅ 이번 페이지가 limit보다 적게 오면 더 없음
      const limit = Number(filters.limit ?? 5);
      if (rawList.length < limit) setHasMore(false);
      else setHasMore(true);

      const mappedBase = rawList.map((b) => ({
        ...b,
        battleStartDate: formatStartDatetime(b.startDatetime),
        courseImage: b.courseImages || null,
      }));

      const mappedWithThumb = await Promise.all(
        mappedBase.map(async (b) => {
          if (b.courseImage) return b;
          const thumb = await getCourseThumb(b.courseId);
          return { ...b, courseImage: thumb };
        })
      );

      const isAppend = Number(filters.offset ?? 0) > 0;

      setBattles((prev) => {
        if (!isAppend) return mappedWithThumb;

        // ✅ 중복 방지(안전장치)
        const seen = new Set(prev.map((x) => x.battleId));
        const onlyNew = mappedWithThumb.filter((x) => !seen.has(x.battleId));
        return [...prev, ...onlyNew];
      });
    } catch (e) {
      console.error("listBattle error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListBattle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  // ✅ 다음 페이지 로딩 트리거
  const loadMore = () => {
    if (loading) return;
    if (!hasMore) return;

    setFilters((prev) => ({
      ...prev,
      offset: Number(prev.offset ?? 0) + Number(prev.limit ?? 5),
    }));
  };

  // ✅ 필터 변경 시(검색/칩 변경 시) 페이지 초기화용 헬퍼
  const resetAndSetFilters = (next) => {
    setHasMore(true);
    setBattles([]);
    setFilters({
      ...next,
      offset: 0,
      limit: next.limit ?? 5,
    });
  };

  return {
    battles,
    loading,
    hasMore,
    filters,
    setFilters,          // 필요하면 그대로도 쓸 수 있게 남김
    setFiltersReset: resetAndSetFilters, // ✅ 필터 UI는 이걸 쓰면 깔끔
    loadMore,
    reload: fetchListBattle,
  };
}
